import React, { useState, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userRequest } from "../utils/requestMethods";
import {
  Header,
  CustomPagination,
  SkeletonTable,
  TableDropdown,
  StudentTestAnswersModal,
  ExtendTestSessionModal,
  ResetTestSessionModal,
} from "../components";
import { useStateContext } from "../contexts/ContextProvider";
import { getSessionEndTime } from "../utils/testSessionUtils";
import { toast } from "react-toastify";
import { FiMoreHorizontal, FiEye, FiCheck, FiClock, FiRefreshCw, FiXCircle, FiSlash } from "react-icons/fi";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      fontWeight: "600",
      fontSize: "14px",
      color: "#475569",
      borderBottom: "2px solid #e2e8f0",
    },
  },
  headCells: {
    style: {
      paddingLeft: "20px",
      paddingRight: "20px",
      paddingTop: "16px",
      paddingBottom: "16px",
      textTransform: "none",
      letterSpacing: "0",
    },
  },
  cells: {
    style: {
      paddingLeft: "20px",
      paddingRight: "20px",
      paddingTop: "12px",
      paddingBottom: "12px",
    },
  },
  rows: {
    style: {
      borderBottom: "1px solid #f1f5f9",
      "&:hover": {
        backgroundColor: "#f8fafc",
      },
    },
  },
};

const TestResults = () => {
  const { testId, schoolId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { school } = useStateContext();

  const [results, setResults] = useState([]);
  const [testDetails, setTestDetails] = useState(location.state?.testDetails || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAnswers, setStudentAnswers] = useState([]);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedStudentForExtend, setSelectedStudentForExtend] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedStudentForReset, setSelectedStudentForReset] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedStudentForApprove, setSelectedStudentForApprove] = useState(null);
  const [showUnapproveModal, setShowUnapproveModal] = useState(false);
  const [selectedStudentForUnapprove, setSelectedStudentForUnapprove] = useState(null);
  const [showBulkUnapproveModal, setShowBulkUnapproveModal] = useState(false);
  const [bulkUnapproving, setBulkUnapproving] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [selectedStudentForEndSession, setSelectedStudentForEndSession] = useState(null);
  const [showBulkEndSessionModal, setShowBulkEndSessionModal] = useState(false);
  const [endSessionReason, setEndSessionReason] = useState("");
  const [endSessionLoading, setEndSessionLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  const fetchTestResults = async () => {
    if (testId && schoolId) {
      try {
        setLoading(true);
        const { data } = await userRequest.get(
          `/test/${testId}/${schoolId}/results?page=1&limit=100`
        );
        //console.log('Test results data:', data);
        setResults(data.results || []);
      } catch (error) {
        console.error("Error fetching test results:", error);
        setError("Failed to fetch test results");
        toast.error("Failed to load test results");
        setTimeout(() => setError(""), 5000);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchTestResults();
  }, [testId, schoolId]);

  const getScoreColor = (currentScore, totalScore) => {
    const percentage = (currentScore / totalScore) * 100;
    if (percentage >= 70) return "text-green-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getGrade = (currentScore, totalScore) => {
    const percentage = (currentScore / totalScore) * 100;
    if (percentage >= 90) return "A+";
    if (percentage >= 80) return "A";
    if (percentage >= 70) return "B";
    if (percentage >= 60) return "C";
    if (percentage >= 50) return "D";
    return "F";
  };

  const formatDuration = (startTime, endTime, createdAt) => {
    if (!startTime || !endTime) return "N/A";
    let start = new Date(startTime);
    const end = new Date(endTime);

    if (start > end && createdAt) {
      start = new Date(createdAt);
    }

    const diffMs = Math.max(0, end - start);
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const fetchStudentAnswers = async (studentId) => {
    try {
      setAnswersLoading(true);
      const { data } = await userRequest.get(`/test/${testId}/answers/${schoolId}/${studentId}`);
      setStudentAnswers(data.answers || []);
      setShowAnswersModal(true);
    } catch (error) {
      console.error("Error fetching student answers:", error);
      toast.error("Failed to fetch student answers");
    } finally {
      setAnswersLoading(false);
    }
  };

  const handleViewAnswers = (student) => {
    setSelectedStudent(student);
    setDropdownIndex(null);
    fetchStudentAnswers(student.child?._id || student.applicant?.submission_id || student._id);
  };

  const handleRefreshAnswers = async () => {
    if (selectedStudent) {
      await fetchStudentAnswers(
        selectedStudent.child?._id ||
          selectedStudent.applicant?.submission_id ||
          selectedStudent._id
      );
      await fetchTestResults();
    }
  };

  const handleApproveResult = (student) => {
    setSelectedStudentForApprove(student);
    setShowApproveModal(true);
    setDropdownIndex(null);
  };

  const confirmApproveResult = async () => {
    if (!selectedStudentForApprove) return;

    setActionLoading(true);
    try {
      // API call to approve individual student result
      await userRequest.patch(`/test/${testId}/answers/${schoolId}/approve`, {
        childIds: [
          selectedStudentForApprove.child?._id ||
            selectedStudentForApprove.applicant?.submission_id ||
            selectedStudentForApprove._id,
        ],
      });

      toast.success("Result approved successfully!");
      // Refresh the results
      fetchTestResults();
    } catch (error) {
      console.error("Error approving result:", error);
      toast.error("Failed to approve result");
    } finally {
      setActionLoading(false);
      setShowApproveModal(false);
      setSelectedStudentForApprove(null);
    }
  };

  const closeApproveModal = () => {
    setShowApproveModal(false);
    setSelectedStudentForApprove(null);
  };

  const handleUnapproveResult = (student) => {
    setSelectedStudentForUnapprove(student);
    setShowUnapproveModal(true);
    setDropdownIndex(null);
  };

  const confirmUnapproveResult = async () => {
    if (!selectedStudentForUnapprove) return;
    setActionLoading(true);
    try {
      await userRequest.patch(`/test/${testId}/answers/${schoolId}/unapprove`, {
        childIds: [
          selectedStudentForUnapprove.child?._id ||
            selectedStudentForUnapprove.applicant?.submission_id ||
            selectedStudentForUnapprove._id,
        ],
      });
      toast.success("Result unapproved successfully!");
      fetchTestResults();
    } catch (error) {
      toast.error("Failed to unapprove result");
    } finally {
      setActionLoading(false);
      setShowUnapproveModal(false);
      setSelectedStudentForUnapprove(null);
    }
  };

  const closeUnapproveModal = () => {
    setShowUnapproveModal(false);
    setSelectedStudentForUnapprove(null);
  };

  const handleBulkUnapprove = async () => {
    setBulkUnapproving(true);
    try {
      const childIds = selectedRows
        .map((row) => row.child?._id || row.applicant?.submission_id || row._id)
        .filter(Boolean);
      await userRequest.patch(`/test/${testId}/answers/${schoolId}/unapprove`, { childIds });
      const count = childIds.length;
      toast.success(`${count} result${count > 1 ? "s" : ""} unapproved successfully!`);
      setShowBulkUnapproveModal(false);
      setSelectedRows([]);
      setToggleCleared(!toggleCleared);
      fetchTestResults();
    } catch (error) {
      toast.error("Failed to unapprove results");
    } finally {
      setBulkUnapproving(false);
    }
  };

  const handleBulkApprove = async () => {
    setBulkApproving(true);
    try {
      // Get all child IDs from the results
      const childIds = results
        .map((result) => result.child?._id || result.applicant?.submission_id || result._id)
        .filter(Boolean);

      // API call to approve all students' results
      await userRequest.patch(`/test/${testId}/answers/${schoolId}/approve`, {
        childIds: childIds,
      });

      toast.success("All results approved successfully!");
      setShowBulkApproveModal(false);
      // Refresh the results
      fetchTestResults();
    } catch (error) {
      console.error("Error approving all results:", error);
      toast.error("Failed to approve all results");
    } finally {
      setBulkApproving(false);
    }
  };

  const openBulkApproveModal = () => {
    setShowBulkApproveModal(true);
  };

  const closeBulkApproveModal = () => {
    setShowBulkApproveModal(false);
  };

  const handleExtendSession = (student) => {
    setSelectedStudentForExtend(student);
    setShowExtendModal(true);
    setDropdownIndex(null);
  };

  const closeExtendModal = () => {
    setShowExtendModal(false);
    setSelectedStudentForExtend(null);
  };

  const handleResetSession = (student) => {
    setSelectedStudentForReset(student);
    setShowResetModal(true);
    setDropdownIndex(null);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setSelectedStudentForReset(null);
  };

  const handleResetSuccess = () => {
    // Refresh the results after successful reset
    fetchTestResults();
  };

  const handleEndSession = (student) => {
    setSelectedStudentForEndSession(student);
    setShowEndSessionModal(true);
    setDropdownIndex(null);
    setEndSessionReason("");
  };

  const closeEndSessionModal = () => {
    setShowEndSessionModal(false);
    setSelectedStudentForEndSession(null);
    setEndSessionReason("");
  };

  const confirmEndSession = async () => {
    if (!selectedStudentForEndSession) return;

    setEndSessionLoading(true);
    try {
      await userRequest.post("/test-session/admin-submit", {
        testId: testId,
        childIds: [
          selectedStudentForEndSession.child?._id ||
            selectedStudentForEndSession.applicant?.submission_id ||
            selectedStudentForEndSession._id,
        ],
        reason: endSessionReason || undefined,
      });

      toast.success("Test session ended successfully!");
      setShowEndSessionModal(false);
      setSelectedStudentForEndSession(null);
      setEndSessionReason("");
      // Refresh the results
      fetchTestResults();
    } catch (error) {
      console.error("Error ending session:", error);
      toast.error(error.response?.data?.message || "Failed to end test session");
    } finally {
      setEndSessionLoading(false);
    }
  };

  const handleRowSelection = (state) => {
    setSelectedRows(state.selectedRows);
  };

  const openBulkEndSessionModal = () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one student to end their session");
      return;
    }
    setShowBulkEndSessionModal(true);
    setEndSessionReason("");
  };

  const closeBulkEndSessionModal = () => {
    setShowBulkEndSessionModal(false);
    setEndSessionReason("");
  };

  const handleBulkEndSession = async () => {
    if (selectedRows.length === 0) return;

    setEndSessionLoading(true);
    try {
      // Get child IDs from selected rows
      const childIds = selectedRows
        .map((row) => row.child?._id || row.applicant?.submission_id || row._id)
        .filter(Boolean);

      await userRequest.post("/test-session/admin-submit", {
        testId: testId,
        childIds: childIds,
        reason: endSessionReason || undefined,
      });

      const count = childIds.length;
      toast.success(`${count} test session${count > 1 ? "s" : ""} ended successfully!`);
      setShowBulkEndSessionModal(false);
      setEndSessionReason("");
      setSelectedRows([]);
      setToggleCleared(!toggleCleared); // Clear selections
      // Refresh the results
      fetchTestResults();
    } catch (error) {
      console.error("Error ending sessions:", error);
      toast.error(error.response?.data?.message || "Failed to end test sessions");
    } finally {
      setEndSessionLoading(false);
    }
  };

  const columns = [
    {
      name: "Student",
      selector: (row) => (
        <div className="py-2">
          <div className="font-semibold text-slate-800 text-sm">
            {row.child
              ? `${row.child.firstname} ${row.child.lastname}`
              : row.applicant?.name || "Unknown"}
          </div>
          <div className="text-xs text-slate-500">
            {row.child?._id || row.applicant?.candidate_number || "N/A"}
          </div>
        </div>
      ),
      width: "200px",
      minWidth: "180px",
    },
    {
      name: "Score",
      selector: (row) => {
        const percentage = ((row.current_score / row.total_score) * 100).toFixed(1);
        return (
          <div className="py-2">
            <div
              className={`font-bold text-lg ${getScoreColor(row.current_score, row.total_score)}`}
            >
              {row.current_score}/{row.total_score}
            </div>
            <div className="text-xs text-slate-500">{percentage}%</div>
          </div>
        );
      },
      width: "120px",
      minWidth: "100px",
    },
    /*{
      name: "Grade",
      selector: row => {
        const grade = getGrade(row.current_score, row.total_score);
        return (
          <div className="py-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              grade === 'A+' || grade === 'A'
                ? 'bg-green-100 text-green-800'
                : grade === 'B'
                ? 'bg-blue-100 text-blue-800'
                : grade === 'C'
                ? 'bg-yellow-100 text-yellow-800'
                : grade === 'D'
                ? 'bg-orange-100 text-orange-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {grade}
            </span>
          </div>
        );
      },
      width: "80px",
      minWidth: "70px"
    },*/
    {
      name: "Progress",
      selector: (row) => (
        <div className="py-2">
          <div className="text-sm text-slate-700">
            {row.answered_questions}/{row.total_questions} questions
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${row.progress}%` }}
            ></div>
          </div>
        </div>
      ),
      width: "140px",
      minWidth: "120px",
    },
    {
      name: "Duration",
      selector: (row) => (
        <div className="py-2">
          <span className="text-sm text-slate-700">
            {formatDuration(row.start_time, getSessionEndTime(row), row.createdAt)}
          </span>
        </div>
      ),
      width: "100px",
      minWidth: "80px",
    },
    {
      name: "Status",
      selector: (row) => (
        <div className="py-2 space-y-1">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              row.status === "completed"
                ? "bg-green-100 text-green-800"
                : row.status === "in_progress"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {row.status?.replace("_", " ").toUpperCase() || "UNKNOWN"}
          </span>
          {row.is_approved && (
            <div className="flex items-center space-x-1">
              <FiCheck className="w-3 h-3 text-green-600" />
              <span className="text-xs text-green-600 font-medium">Approved</span>
            </div>
          )}
        </div>
      ),
      width: "120px",
      minWidth: "100px",
    },
    {
      name: "Started",
      selector: (row) => (
        <div className="py-2">
          <span className="text-xs text-slate-500">
            {row.start_time ? new Date(row.start_time).toLocaleDateString() : "N/A"}
          </span>
          {row.start_time && (
            <div className="text-xs text-slate-400">
              {new Date(row.start_time).toLocaleTimeString()}
            </div>
          )}
          {getSessionEndTime(row) && <div className="text-xs text-center text-slate-400">To:</div>}
          {getSessionEndTime(row) && (
            <div className="text-xs text-slate-400">
              {new Date(getSessionEndTime(row)).toLocaleTimeString()}
            </div>
          )}
        </div>
      ),
      width: "140px",
      minWidth: "120px",
    },
    {
      name: "Options",
      cell: (row, rowIndex) => (
        <div className="relative py-2">
          <button
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View Answers",
                icon: <FiEye className="w-4 h-4 text-blue-600" />,
                onClick: () => handleViewAnswers(row),
              },
              {
                label: "Approve Result",
                icon: <FiCheck className="w-4 h-4 text-green-600" />,
                onClick: () => handleApproveResult(row),
              },
              ...(row.is_approved ? [{
                label: "Unapprove Result",
                icon: <FiSlash className="w-4 h-4 text-orange-500" />,
                onClick: () => handleUnapproveResult(row),
              }] : []),
              {
                label: "Extend Session",
                icon: <FiClock className="w-4 h-4 text-orange-600" />,
                onClick: () => handleExtendSession(row),
                disabled: actionLoading,
              },
              {
                label: "Reset student Device",
                icon: <FiRefreshCw className="w-4 h-4 text-purple-600" />,
                onClick: () => handleResetSession(row),
                disabled: actionLoading,
              },
              {
                label: "End Session",
                icon: <FiXCircle className="w-4 h-4 text-red-600" />,
                onClick: () => handleEndSession(row),
                disabled: actionLoading || endSessionLoading,
              },
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
      minWidth: "100px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  // Filter results based on search text
  const filteredResults = results.filter((result) => {
    const childFirstname = result.child?.firstname?.toLowerCase() || "";
    const childLastname = result.child?.lastname?.toLowerCase() || "";
    const childId = result.child?._id?.toLowerCase() || "";
    const applicantName = result.applicant?.name?.toLowerCase() || "";
    const applicantCandidateNumber = result.applicant?.candidate_number?.toLowerCase() || "";
    const search = searchText.toLowerCase();

    return (
      childFirstname.includes(search) ||
      childLastname.includes(search) ||
      childId.includes(search) ||
      applicantName.includes(search) ||
      applicantCandidateNumber.includes(search)
    );
  });

  // Calculate statistics
  const totalStudents = results.length;
  const completedTests = results.filter((r) => r.status === "completed").length;
  const averageScore =
    results.length > 0
      ? (results.reduce((sum, r) => sum + r.current_score, 0) / results.length).toFixed(1)
      : 0;
  const passRate =
    results.length > 0
      ? (
          (results.filter((r) => (r.current_score / r.total_score) * 100 >= 50).length /
            results.length) *
          100
        ).toFixed(1)
      : 0;

  return (
    <>
      <div className="px-10 pt-6">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => history.goBack()}
            className="flex items-center text-slate-600 hover:text-slate-800 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        </div>
        <Header category="CBT Exams" title="Test Results" />
      </div>

      {/* Test Details */}
      {testDetails && (
        <div className="px-10 pt-5">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">{testDetails.title}</h1>
                {testDetails.description && (
                  <p className="text-slate-600 text-base mb-4 leading-relaxed">
                    {testDetails.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    {testDetails.subject?.subject_name}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    {testDetails.class?.class_name}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-100 text-amber-700 border border-amber-200">
                    {testDetails.type?.toUpperCase() || "EXAM"}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Total Score</div>
                  <div className="text-lg font-bold text-slate-800">{testDetails.total_score}</div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Duration</div>
                  <div className="text-lg font-bold text-slate-800">{testDetails.duration} min</div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Students</div>
                  <div className="text-lg font-bold text-slate-800">{totalStudents}</div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Completed</div>
                  <div className="text-lg font-bold text-slate-800">{completedTests}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
        {/* Search and Actions */}
        <div className="flex flex-col gap-4 mb-8">
          {/* Title row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Student Results</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {filteredResults.length} of {results.length} student{results.length !== 1 ? "s" : ""}
                {searchText && <span className="text-blue-500"> · filtered</span>}
              </p>
            </div>

            {/* Search */}
            <div className="relative sm:w-72">
              <input
                type="text"
                placeholder="Search by name or candidate number..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50 transition-all"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Action buttons row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Approve All — global action */}
            <button
              onClick={openBulkApproveModal}
              disabled={bulkApproving || results.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
            >
              {bulkApproving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <FiCheck className="w-4 h-4" />
              )}
              {bulkApproving ? "Approving..." : "Approve All"}
            </button>

            {/* Divider */}
            <div className="w-px h-6 bg-slate-200 mx-1" />

            {/* Selection-based actions — shown always, disabled until rows selected */}
            <div className="flex items-center gap-2">
              {selectedRows.length > 0 && (
                <span className="text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">
                  {selectedRows.length} selected
                </span>
              )}
              <button
                onClick={() => selectedRows.length > 0 && setShowBulkUnapproveModal(true)}
                disabled={bulkUnapproving || selectedRows.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-orange-50 border border-orange-200 disabled:opacity-40 disabled:cursor-not-allowed text-orange-600 text-sm font-medium rounded-lg transition-colors"
              >
                {bulkUnapproving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent" />
                ) : (
                  <FiSlash className="w-4 h-4" />
                )}
                {bulkUnapproving ? "Unapproving..." : "Unapprove"}
              </button>
              <button
                onClick={openBulkEndSessionModal}
                disabled={endSessionLoading || selectedRows.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-red-50 border border-red-200 disabled:opacity-40 disabled:cursor-not-allowed text-red-600 text-sm font-medium rounded-lg transition-colors"
              >
                {endSessionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent" />
                ) : (
                  <FiXCircle className="w-4 h-4" />
                )}
                {endSessionLoading ? "Ending..." : "End Session"}
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Average Score</p>
                <p className="text-2xl font-bold text-slate-800">{averageScore}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pass Rate</p>
                <p className="text-2xl font-bold text-slate-800">{passRate}%</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Completed</p>
                <p className="text-2xl font-bold text-slate-800">
                  {completedTests}/{totalStudents}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Students</p>
                <p className="text-2xl font-bold text-slate-800">{totalStudents}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Results Table */}
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm bg-white">
          <div className="overflow-x-auto">
            <div className="min-w-full" style={{ minWidth: "800px" }}>
              <DataTable
                data={filteredResults}
                columns={columns}
                getRowId={(row) =>
                  row._id ||
                  row.child?._id ||
                  row.applicant?.submission_id ||
                  `${row.child?.firstname || row.applicant?.name || ""}-${
                    row.child?.lastname || ""
                  }`
                }
                pagination
                paginationComponent={CustomPagination}
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                customStyles={customStyles}
                progressPending={loading}
                progressComponent={<SkeletonTable rows={5} columns={7} />}
                highlightOnHover={true}
                striped={true}
                selectableRows
                selectableRowsHighlight
                selectableRowsVisibleOnly
                clearSelectedRows={toggleCleared}
                onSelectedRowsChange={handleRowSelection}
                noDataComponent={
                  <div className="text-center py-16">
                    <div className="mx-auto h-24 w-24 text-slate-300 mb-6 flex items-center justify-center">
                      <svg
                        className="w-16 h-16"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No results found</h3>
                    <p className="text-slate-500 text-sm">
                      {searchText
                        ? "No students match your search criteria"
                        : "No students have taken this test yet"}
                    </p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Student Answers Modal */}
      {showAnswersModal && selectedStudent && (
        <StudentTestAnswersModal
          isVisible={showAnswersModal}
          onClose={() => {
            setShowAnswersModal(false);
            setSelectedStudent(null);
            setStudentAnswers([]);
          }}
          student={selectedStudent}
          testDetails={testDetails}
          answers={studentAnswers}
          loading={answersLoading}
          onApprove={handleApproveResult}
          onMarkSuccess={fetchTestResults}
          sessionId={selectedStudent?._id}
        />
      )}

      {/* Extend Test Session Modal */}
      {showExtendModal && selectedStudentForExtend && (
        <ExtendTestSessionModal
          isVisible={showExtendModal}
          onClose={closeExtendModal}
          student={selectedStudentForExtend}
          testDetails={testDetails}
          testId={testId}
        />
      )}

      {/* Reset Test Session Modal */}
      {showResetModal && selectedStudentForReset && (
        <ResetTestSessionModal
          isVisible={showResetModal}
          onClose={closeResetModal}
          student={selectedStudentForReset}
          testDetails={testDetails}
          onSuccess={handleResetSuccess}
        />
      )}

      {/* Individual Approve Result Confirmation Modal */}
      {showApproveModal && selectedStudentForApprove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <FiCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Approve Student Result</h3>
                <p className="text-sm text-slate-500">
                  Confirm approval for this student's test result
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-2">Student Details:</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Name:</span>
                  <span className="font-semibold">
                    {selectedStudentForApprove?.child
                      ? `${selectedStudentForApprove.child.firstname} ${selectedStudentForApprove.child.lastname}`
                      : selectedStudentForApprove?.applicant?.name || ""}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Score:</span>
                  <span className="font-semibold">
                    {selectedStudentForApprove?.current_score || 0}/
                    {selectedStudentForApprove?.total_score || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="font-semibold">
                    {selectedStudentForApprove?.status?.replace("_", " ").toUpperCase() ||
                      "UNKNOWN"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Progress:</span>
                  <span className="font-semibold">
                    {selectedStudentForApprove?.answered_questions || 0}/
                    {selectedStudentForApprove?.total_questions || 0} questions
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeApproveModal}
                disabled={actionLoading}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproveResult}
                disabled={actionLoading}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                {actionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Approving...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4 mr-2" />
                    Approve Result
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Unapprove Result Confirmation Modal */}
      {showUnapproveModal && selectedStudentForUnapprove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <FiSlash className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Unapprove Result</h3>
                <p className="text-sm text-slate-500">This will mark the result as unapproved</p>
              </div>
            </div>
            <div className="mb-6 p-4 bg-slate-50 rounded-lg text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-600">Name:</span>
                <span className="font-semibold">
                  {selectedStudentForUnapprove?.child
                    ? `${selectedStudentForUnapprove.child.firstname} ${selectedStudentForUnapprove.child.lastname}`
                    : selectedStudentForUnapprove?.applicant?.name || ""}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Score:</span>
                <span className="font-semibold">
                  {selectedStudentForUnapprove?.current_score ?? 0}/{selectedStudentForUnapprove?.total_score ?? 0}
                </span>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={closeUnapproveModal} disabled={actionLoading} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={confirmUnapproveResult}
                disabled={actionLoading}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                {actionLoading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Unapproving...</>
                ) : (
                  <><FiSlash className="w-4 h-4" />Unapprove Result</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Unapprove Confirmation Modal */}
      {showBulkUnapproveModal && selectedRows.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <FiSlash className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Unapprove Results</h3>
                <p className="text-sm text-slate-500">Unapprove results for {selectedRows.length} selected student{selectedRows.length > 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-3 font-medium">Selected Students ({selectedRows.length}):</div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedRows.map((row, index) => (
                  <div key={row._id || index} className="flex justify-between items-center text-sm p-2 bg-white rounded border border-slate-200">
                    <div>
                      <div className="font-medium text-slate-800">
                        {row.child ? `${row.child.firstname} ${row.child.lastname}` : row.applicant?.name || "Unknown"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Score: {row.current_score ?? 0}/{row.total_score ?? 0} · {row.is_approved ? "Approved" : "Not approved"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowBulkUnapproveModal(false)} disabled={bulkUnapproving} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={handleBulkUnapprove}
                disabled={bulkUnapproving}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                {bulkUnapproving ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Unapproving...</>
                ) : (
                  <><FiSlash className="w-4 h-4" />Unapprove {selectedRows.length} Result{selectedRows.length > 1 ? "s" : ""}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Approve Confirmation Modal */}
      {showBulkApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <FiCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Approve All Results</h3>
                <p className="text-sm text-slate-500">
                  This action will approve all student results for this test
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-2">Summary:</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Total Students:</span>
                  <span className="font-semibold">{results.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Completed Tests:</span>
                  <span className="font-semibold">
                    {results.filter((r) => r.status === "completed").length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Already Approved:</span>
                  <span className="font-semibold">
                    {results.filter((r) => r.is_approved).length}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeBulkApproveModal}
                disabled={bulkApproving}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkApprove}
                disabled={bulkApproving}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                {bulkApproving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Approving...
                  </>
                ) : (
                  "Approve All"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual End Session Confirmation Modal */}
      {showEndSessionModal && selectedStudentForEndSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <FiXCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">End Test Session</h3>
                <p className="text-sm text-slate-500">Submit test on behalf of this student</p>
              </div>
            </div>

            <div className="mb-4 p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-2">Student Details:</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Name:</span>
                  <span className="font-semibold">
                    {selectedStudentForEndSession?.child
                      ? `${selectedStudentForEndSession.child.firstname} ${selectedStudentForEndSession.child.lastname}`
                      : selectedStudentForEndSession?.applicant?.name || ""}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Score:</span>
                  <span className="font-semibold">
                    {selectedStudentForEndSession?.current_score || 0}/
                    {selectedStudentForEndSession?.total_score || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="font-semibold">
                    {selectedStudentForEndSession?.status?.replace("_", " ").toUpperCase() ||
                      "UNKNOWN"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Progress:</span>
                  <span className="font-semibold">
                    {selectedStudentForEndSession?.answered_questions || 0}/
                    {selectedStudentForEndSession?.total_questions || 0} questions
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={endSessionReason}
                onChange={(e) => setEndSessionReason(e.target.value)}
                placeholder="e.g., Student's device crashed after answering questions"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows="3"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeEndSessionModal}
                disabled={endSessionLoading}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmEndSession}
                disabled={endSessionLoading}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                {endSessionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ending...
                  </>
                ) : (
                  <>
                    <FiXCircle className="w-4 h-4 mr-2" />
                    End Session
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk End Session Confirmation Modal */}
      {showBulkEndSessionModal && selectedRows.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <FiXCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">End Test Sessions</h3>
                <p className="text-sm text-slate-500">
                  Submit tests on behalf of selected students
                </p>
              </div>
            </div>

            <div className="mb-4 p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-3 font-medium">
                Selected Students ({selectedRows.length}):
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedRows.map((row, index) => (
                  <div
                    key={row._id || row.student?._id || index}
                    className="flex justify-between items-center text-sm p-2 bg-white rounded border border-slate-200"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">
                        {row.child
                          ? `${row.child.firstname} ${row.child.lastname}`
                          : row.applicant?.name || "Unknown"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Score: {row.current_score || 0}/{row.total_score || 0} •{" "}
                        {row.status?.replace("_", " ").toUpperCase() || "UNKNOWN"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Total Selected:</span>
                  <span className="font-semibold">{selectedRows.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>In Progress:</span>
                  <span className="font-semibold">
                    {selectedRows.filter((r) => r.status === "in_progress").length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Completed:</span>
                  <span className="font-semibold">
                    {selectedRows.filter((r) => r.status === "completed").length}
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={endSessionReason}
                onChange={(e) => setEndSessionReason(e.target.value)}
                placeholder="e.g., Student's device crashed after answering questions"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows="3"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeBulkEndSessionModal}
                disabled={endSessionLoading}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEndSession}
                disabled={endSessionLoading}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                {endSessionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ending...
                  </>
                ) : (
                  <>
                    <FiXCircle className="w-4 h-4 mr-2" />
                    End {selectedRows.length} Session{selectedRows.length > 1 ? "s" : ""}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TestResults;
